import { getAccountById } from "api/services/AccountService";
import { getAllContactsList, getAllContractGroupsList } from "api/services/ContactService";
import { getAllDocumentTypesList } from "api/services/DocumentTypeService";
import { getAllLocationsList, getAllRegionsList } from "api/services/LocationService";
import {
  TAccount,
  TContactPicklist,
  TContractGroupPicklist,
  TDocumentTypePicklist,
  TLocationPicklist,
  TProviderPicklist,
  TRegionPicklist,
  TRole,
  TSpecialityPicklist,
  TUser
} from "api/services/models/types";
import { getAllProvidersList } from "api/services/ProviderService";
import { getAllSpecialityList } from "api/services/SpecialityService";
import { getAllRolesList } from "api/services/UserService";
import { useTDAuthentication } from "auth/AuthenticationProvider";
import { AxiosError } from "axios";
import ErrorViewer from "components/ErrorViewer";
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

export type TPicklistContext = {
  loading: boolean;
  error: AxiosError | null;
  account?: TAccount;
  regions?: TRegionPicklist[];
  locations?: TLocationPicklist[];
  contacts?: TContactPicklist[];
  providers?: TProviderPicklist[];
  roles?: TRole[];
  documentTypes?: TDocumentTypePicklist[];
  specialities?: TSpecialityPicklist[];
  users?: TUser[];
  contactGroups?: TContractGroupPicklist[];
  refresh?: (entity?: TRefreshEntity) => void;
};

const PicklistContext = createContext<TPicklistContext>({ loading: false, error: null });
type TRefreshEntity =
  | "Account"
  | "Contacts"
  | "Providers"
  | "Locations"
  | "Roles"
  | "Regions"
  | "DocumentTypes"
  | "Specialities"
  | "Users";

export const PicklistContextProvider = ({ children }: { children: React.ReactNode }) => {
  const { accountId } = useTDAuthentication();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<AxiosError | null>(null);
  const [account, setAccount] = useState<TAccount>();
  const [regions, setRegions] = useState<TRegionPicklist[]>();
  const [locations, setLocations] = useState<TLocationPicklist[]>([]);
  const [providers, setProviders] = useState<TProviderPicklist[]>([]);
  const [contacts, setContacts] = useState<TContactPicklist[]>([]);
  const [documentTypes, setDocumentTypes] = useState<TDocumentTypePicklist[]>([]);
  const [roles, setRoles] = useState<TRole[]>([]);
  const [specialities, setSpecialities] = useState<TSpecialityPicklist[]>([]);
  const [users, setUsers] = useState<TUser[]>([]);
  const [contactGroups, setContactGroups] = useState<TContractGroupPicklist[]>([]);

  const refresh = useCallback(
    (entity?: TRefreshEntity) => {
      if (accountId) {
        setLoading(true);
        setError(null);
        Promise.all([
          entity == null || entity === "Account"
            ? getAccountById(accountId)
            : Promise.resolve(),
          entity == null || entity === "Locations"
            ? getAllLocationsList(accountId)
            : Promise.resolve(),
          entity == null || entity === "Providers"
            ? getAllProvidersList(accountId)
            : Promise.resolve(),
          entity == null || entity === "Contacts"
            ? getAllContactsList(accountId)
            : Promise.resolve(),
          entity == null || entity === "Roles" ? getAllRolesList(accountId) : Promise.resolve(),
          entity == null || entity === "Regions"
            ? getAllRegionsList(accountId)
            : Promise.resolve(),
          entity == null || entity === "DocumentTypes"
            ? getAllDocumentTypesList(accountId)
            : Promise.resolve(),
          entity == null || entity === "Specialities"
            ? getAllSpecialityList()
            : Promise.resolve(),
          getAllContractGroupsList()
        ])
          .then(results => {
            if (results[0]) setAccount(results[0]);
            if (results[1]) setLocations(results[1]);
            if (results[2]) setProviders(results[2]);
            if (results[3])
              setContacts(
                results[3]?.toSorted((c1, c2) => c1.contactName.localeCompare(c2.contactName))
              );
            if (results[4]) setRoles(results[4]);
            if (results[5]) setRegions(results[5]);
            if (results[6]) setDocumentTypes(results[6]);
            if (results[7]) setSpecialities(results[7]);
            if (results[8]) setContactGroups(results[8]);
          })
          .catch(setError)
          .finally(() => setLoading(false));
      }
    },
    [accountId]
  );

  useEffect(() => refresh(), [accountId]);

  const picklistContextValue = useMemo(
    () => ({
      loading,
      error,
      account,
      regions,
      locations,
      providers,
      contacts,
      roles,
      documentTypes,
      specialities,
      users,
      contactGroups,
      refresh
    }),
    [
      loading,
      error,
      account,
      locations,
      providers,
      contacts,
      roles,
      documentTypes,
      specialities,
      users,
      contactGroups,
      refresh
    ]
  );
  return (
    <PicklistContext.Provider value={picklistContextValue}>
      {error == null && children}
      {error != null && <ErrorViewer error={error} />}
    </PicklistContext.Provider>
  );
};

export function usePicklists() {
  const context = useContext(PicklistContext);
  if (context == null) {
    throw new Error("Invalid picklists context");
  }
  return context;
}
